import React, { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';
import { supabase, isConfigured } from './services/supabase';
import type { AppView } from './types';

import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import LandingHero from './components/LandingHero';
import Dashboard from './components/Dashboard';
import BrandedLanding from './components/BrandedLanding';
import FluidBackground from './components/FluidBackground';
import PricingPage from './components/PricingPage';

/**
 * Detect the public branded route (/v/:slug) before any auth wiring
 * runs. Branded pages are anonymous-only — no login modal, no dashboard.
 */
function getBrandedRoute(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/v\/([a-z0-9-]{2,32})\/?$/i);
  return m ? m[1].toLowerCase() : null;
}

function isPricingRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return /^\/pricing\/?$/i.test(window.location.pathname);
}

export default function App() {
  const brandedSlug = getBrandedRoute();
  const onPricingPage = isPricingRoute();

  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState<AppView>('LOADING');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  useEffect(() => {
    if (brandedSlug) {
      // Branded page is anon — skip auth machinery entirely.
      setView('LANDING');
      return;
    }
    if (!isConfigured) {
      setView('LANDING');
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setUser(data.session?.user ?? null);
      setView(data.session?.user ? 'DASHBOARD' : 'LANDING');
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_, s) => {
      setSession(s ?? null);
      setUser(s?.user ?? null);
      setView(s?.user ? 'DASHBOARD' : 'LANDING');
    });
    return () => sub.subscription.unsubscribe();
  }, [brandedSlug]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setView('LANDING');
  };

  const openLogin = () => {
    setAuthMode('login');
    setShowLoginModal(true);
  };
  const openSignUp = () => {
    setAuthMode('signup');
    setShowLoginModal(true);
  };

  if (brandedSlug) {
    return <BrandedLanding slug={brandedSlug} />;
  }

  if (onPricingPage) {
    return <PricingPage />;
  }

  if (view === 'LOADING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950" style={{ color: '#f47a6b' }}>
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  const isLanding = view === 'LANDING';

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 relative overflow-hidden">
      {/* Mouse-reactive coral background, only on the unbranded landing
          page — the dashboard is a working surface, branded pages have
          their own brand. */}
      {isLanding && <FluidBackground />}

      <div className="relative z-10 flex flex-col flex-1 min-h-0">
        <Header
          user={user}
          onLogin={openLogin}
          onSignUp={openSignUp}
          onSignOut={handleSignOut}
        />

        {view === 'LANDING' && <LandingHero onSignUp={openSignUp} />}
        {view === 'DASHBOARD' && user && <Dashboard user={user} />}

        <Footer />
      </div>

      {showLoginModal && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowLoginModal(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
            <Login
              onAuthSuccess={() => {
                setShowLoginModal(false);
              }}
              onClose={() => setShowLoginModal(false)}
              initialMode={authMode}
            />
          </div>
        </div>
      )}
    </div>
  );
}
